import { Controller } from "@hotwired/stimulus"
import { loadPdfLibrary } from "../lib/pdf_library"

const MAX_CANVAS_PIXELS = 0
const TEXT_LAYER_MODE = 1

export default class extends Controller {
  static targets = ["viewerContainer"]
  static values = { url: String }

  currentPage = 1
  pdf = null
  pdfViewer = null
  pdfDocument = null
  resizeTimeout = null
  generation = 0
  boundHandleResize = this.handleResize.bind(this)

  connect() {
    this.connected = true
    this.currentPage = 1
    window.addEventListener('resize', this.boundHandleResize)
    document.documentElement.style.setProperty('--page-border', '0px solid transparent')
    if (this.urlValue) this.createPdfViewer()
  }

  urlValueChanged() {
    if (this.connected && this.urlValue) this.createPdfViewer()
  }

  disconnect() {
    this.connected = false
    this.generation += 1
    window.removeEventListener('resize', this.boundHandleResize)
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = null
    this.releaseDocument()
    this.loadPromise = null
    this.viewerContainerTarget.replaceChildren()
  }

  handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => this.resizeToFitContainer(), 100)
  }

  releaseDocument() {
    this.pdfViewer?.setDocument(null)
    this.viewerAbort?.abort()
    this.viewerAbort = null
    this.pdfViewer = null
    // A loading task owns both the worker and its resolved document.
    const loadingTask = this.loadingTask
    this.loadingTask = null
    this.pdfDocument = null
    loadingTask?.destroy().catch(() => {})
    this.pdf?.destroy().catch(() => {})
    this.pdf = null
  }

  createPdfViewer() {
    if (!this.connected || !this.urlValue) return
    const url = this.urlValue
    if (this.loadedUrl === url && (this.loadPromise || this.pdfDocument)) return this.loadPromise
    this.releaseDocument()
    this.loadedUrl = url
    const generation = ++this.generation
    this.loadPromise = this.loadViewer(url, generation)
    return this.loadPromise
  }

  async loadViewer(url, generation) {
    try {
      const library = await loadPdfLibrary()
      if (!this.connected || generation !== this.generation) return
      library.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${library.version}/build/pdf.worker.min.mjs`
      const eventBus = new library.EventBus()
      const linkService = new library.PDFLinkService({ eventBus })
      const container = this.viewerContainerTarget
      const viewer = document.createElement('div')
      viewer.id = 'viewer'
      viewer.classList.add('pdfViewer')
      container.replaceChildren(viewer)
      this.viewerAbort = new AbortController()
      const pdfViewer = new library.PDFViewer({
        container, eventBus, linkService, l10n: new library.GenericL10n(),
        abortSignal: this.viewerAbort.signal,
        renderer: 'canvas', useOnlyCssZoom: true,
        maxCanvasPixels: MAX_CANVAS_PIXELS, textLayerMode: TEXT_LAYER_MODE,
        removePageBorders: true,
      })
      this.pdfViewer = pdfViewer
      eventBus.on('pagesinit', () => {
        if (!this.connected || generation !== this.generation) return
        pdfViewer.currentScaleValue = 'page-fit'
        this.element.dispatchEvent(new CustomEvent('pdf-viewer:pages-ready', { bubbles: true }))
      })
      eventBus.on('textlayerrendered', event => {
        if (!this.connected || generation !== this.generation) return
        this.element.dispatchEvent(new CustomEvent('pdf-viewer:page-rendered', {
          bubbles: true, detail: { pageNumber: event.pageNumber }
        }))
      })
      const loadingTask = library.getDocument(url)
      this.loadingTask = loadingTask
      const pdfDocument = await loadingTask.promise
      if (!this.connected || generation !== this.generation) return
      this.pdfDocument = pdfDocument
      linkService.setDocument(pdfDocument)
      pdfViewer.setDocument(pdfDocument)
    } catch (error) {
      if (!this.connected || generation !== this.generation) return
      this.releaseDocument()
      this.showError()
    } finally {
      if (generation === this.generation) this.loadPromise = null
    }
  }

  showError() {
    const message = document.createElement('p')
    message.textContent = 'Unable to load this PDF. Try again.'
    message.setAttribute('role', 'alert')
    const retry = document.createElement('button')
    retry.type = 'button'
    retry.className = 'btn btn-sm btn-ghost'
    retry.textContent = 'Retry'
    retry.dataset.action = 'click->pdf-viewer#createPdfViewer'
    this.viewerContainerTarget.replaceChildren(message, retry)
  }

  resizeToFitContainer() {
    if (!this.pdfViewer) return
    this.pdfViewer.currentScaleValue = "page-fit";
  }

  async loadPdf() {
    const library = await loadPdfLibrary()
    library.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${library.version}/build/pdf.worker.min.mjs`
    const loadingTask = library.getDocument(this.urlValue)
    const _pdf = await loadingTask.promise
    this.pdf?.destroy()
    this.pdf = _pdf
    console.log(this.pdf)
    this.renderAllPages()
  }

  async renderAllPages() {
    for (let i = 1; i <= this.pdf.numPages; i++) {
      this.currentPage = i
      await this.renderPage(i)
    }
  }

  async renderPage(pageNumber) {
    const page = await this.pdf.getPage(pageNumber)
    const viewport = page.getViewport({ scale: 1.0 })

    let pageContainer = this.viewerContainerTarget.querySelector(`[data-page-number="${pageNumber}"]`)
    let canvas

    if (pageContainer) {
      canvas = pageContainer.querySelector('canvas')
    } else {
      pageContainer = document.createElement("div")
      pageContainer.classList.add("page-container", "flex", "justify-center", "max-w-[1200px]", "mx-auto")
      pageContainer.dataset.pageNumber = pageNumber
      this.viewerContainerTarget.appendChild(pageContainer)

      canvas = document.createElement("canvas")
      canvas.classList.add("w-full")
      pageContainer.appendChild(canvas)
    }

    canvas.width = viewport.width
    canvas.height = viewport.height

    const context = canvas.getContext("2d")
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    }
    await page.render(renderContext)
  }

}
